"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { useTerminalStore } from "@/lib/terminal/store";
import { cn } from "@/lib/utils";

export default function TerminalInput() {
  const runCommand = useTerminalStore((s) => s.runCommand);
  const clear = useTerminalStore((s) => s.clear);
  const history = useTerminalStore((s) => s.commandHistory);
  // The store's inputDraft is the single source of truth for the field so
  // external surfaces (DSL Explorer's "Insert into terminal") can populate it.
  const value = useTerminalStore((s) => s.inputDraft);
  const setValue = useTerminalStore((s) => s.setInputDraft);
  const cursorRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Focus the field whenever text is injected externally (Insert into terminal).
  useEffect(() => {
    if (value) inputRef.current?.focus();
  }, [value]);

  const submit = useCallback(async () => {
    const text = value;
    if (!text.trim()) return;
    setValue("");
    cursorRef.current = null;
    await runCommand(text);
  }, [value, runCommand]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void submit();
        return;
      }

      if (e.key === "ArrowUp") {
        if (history.length === 0) return;
        e.preventDefault();
        const next =
          cursorRef.current === null
            ? history.length - 1
            : Math.max(0, cursorRef.current - 1);
        cursorRef.current = next;
        setValue(history[next] ?? "");
        return;
      }

      if (e.key === "ArrowDown") {
        if (cursorRef.current === null) return;
        e.preventDefault();
        const next = cursorRef.current + 1;
        if (next >= history.length) {
          cursorRef.current = null;
          setValue("");
        } else {
          cursorRef.current = next;
          setValue(history[next] ?? "");
        }
        return;
      }

      if (e.key === "l" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        clear();
      }
    },
    [history, submit, clear]
  );

  return (
    <div className="flex items-center gap-2 border-t border-border bg-background px-2 py-1.5">
      <span className="text-xs font-mono text-muted-foreground select-none">&gt;</span>
      <input
        ref={inputRef}
        type="text"
        spellCheck={false}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          cursorRef.current = null;
        }}
        onKeyDown={handleKeyDown}
        placeholder={'help or Watchlist.Symbol.Price("EURUSD")'}
        className={cn(
          "flex-1 bg-transparent text-xs font-mono text-foreground",
          "placeholder:text-muted-foreground focus:outline-none"
        )}
        aria-label="Terminal command input"
      />
    </div>
  );
}
